import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import { log } from 'core/helpers'
import i18n from 'core/services/i18n'
import { CLOUDFLARE_TURNSTILE_ACTION } from '../services/challenge'
import { ValidationError } from '../services/validation'

const challengeError = () => new ValidationError([{ field: 'base', error: i18n.t('errors.verification_failed') }])

const loadCloudflareTurnstileScript = (onLoad: () => void) => {
  const script = document.createElement('script')

  script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
  script.defer = !0
  script.async = !0
  script.onload = onLoad

  document.head.appendChild(script)
}

const useCloudflareTurnstile = (active: boolean, siteKey?: string) => {
  const [container, setContainer] = useState<HTMLElement | null>(null)
  const containerRef = useRef<HTMLElement | null>(null)
  const [interactive, setInteractive] = useState(false)
  const [cloudflareTurnstileInstance, setCloudflareTurnstileInstance] = useState<typeof window.turnstile>()
  const widgetIdRef = useRef<string>()
  const resolveRef = useRef<((token: string | undefined) => void) | null>(null)
  const rejectRef = useRef<((error: unknown) => void) | null>(null)

  const settle = (token: string | undefined) => {
    setInteractive(false)

    const resolve = resolveRef.current
    resolveRef.current = null
    rejectRef.current = null
    resolve?.(token)
  }

  const fail = () => {
    setInteractive(false)

    const reject = rejectRef.current
    resolveRef.current = null
    rejectRef.current = null
    reject?.(challengeError())
  }

  useEffect(() => {
    if (!active || !siteKey || !container) return

    let cancelled = false

    const onLoad = () => {
      if (cancelled) return

      if (!window || !window.turnstile) {
        log('Cloudflare Turnstile script is not available')
        return
      }

      widgetIdRef.current = window.turnstile.render(container, {
        sitekey: siteKey,
        action: CLOUDFLARE_TURNSTILE_ACTION,
        execution: 'execute',
        appearance: 'interaction-only',
        callback: token => settle(token),
        'error-callback': () => {
          log('Cloudflare Turnstile challenge failed')
          fail()
          return true
        },
        'timeout-callback': () => fail(),
        'expired-callback': () => fail(),
        'before-interactive-callback': () => setInteractive(true),
        'after-interactive-callback': () => setInteractive(false)
      })

      setCloudflareTurnstileInstance(window.turnstile)
    }

    if (window.turnstile) {
      onLoad()
    } else {
      loadCloudflareTurnstileScript(onLoad)
    }

    return () => {
      cancelled = true

      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = undefined
      }
    }
  }, [active, siteKey, container])

  const executeCloudflareTurnstile = useCallback(
    (action: string): Promise<string | undefined> => {
      if (!active) {
        return Promise.resolve(undefined)
      }

      const widgetId = widgetIdRef.current

      if (!cloudflareTurnstileInstance || !widgetId) {
        log('Cloudflare Turnstile has not been loaded')
        return Promise.reject(challengeError())
      }

      return new Promise<string | undefined>((resolve, reject) => {
        resolveRef.current = resolve
        rejectRef.current = reject
        cloudflareTurnstileInstance.reset(widgetId)
        cloudflareTurnstileInstance.execute(widgetId, { action })
      })
    },
    [active, cloudflareTurnstileInstance]
  )

  const cloudflareTurnstileRef = useCallback((node: HTMLElement | null) => {
    if (node) {
      if (containerRef.current?.isConnected) return

      containerRef.current = node
      setContainer(node)
      return
    }

    if (containerRef.current && !containerRef.current.isConnected) {
      containerRef.current = null
      setContainer(null)
    }
  }, [])

  return {
    cloudflareTurnstileRef,
    cloudflareTurnstileInteractive: interactive,
    executeCloudflareTurnstile
  }
}

export default useCloudflareTurnstile
